// Freunde im Run: Level (F12), Level-Äste (F35), Items (I1) und was davon im Kampf ankommt.
// Alles kommt aus den Bausteinen in data/ (X3): stat, param, kniff, perk, run.
import {
  baseItemId,
  combatRules,
  fillNumbers,
  getAbility,
  getArtifact,
  getClass,
  friendStats,
  getItem,
  getLevelTree,
  itemEffects,
  itemUse,
  getSpecies,
  runRules,
  type Effect,
  type LevelBranch,
  type LevelNode,
  type RunRules,
  type UnitStats,
  defaultKniff,
} from '../../content/index.ts';
import type { CombatProfile } from '../types.ts';
import type { RunFriend, RunState } from './types.ts';

/** F12: XP bis zum nächsten Level: 30 + 15 × (Level − 1) [ZAHL?] */
export function xpToNext(level: number, rules: RunRules = runRules): number {
  return rules.xp.base + rules.xp.step * (level - 1);
}

/** F12: Level aus den XP insgesamt, höchstens 15 */
export function levelForXp(xp: number, rules: RunRules = runRules): number {
  let level = 1;
  let need = xpToNext(1, rules);
  let rest = xp;
  while (rest >= need && level < rules.xp.maxLevel) {
    rest -= need;
    level += 1;
    need = xpToNext(level, rules);
  }
  return level;
}

/** XP, die man für dieses Level insgesamt braucht */
export function xpForLevel(level: number, rules: RunRules = runRules): number {
  let sum = 0;
  for (let l = 1; l < level; l++) sum += xpToNext(l, rules);
  return sum;
}

/** F35: der Baum dieses Freundes; ohne eigenen Baum gibt es nur die allgemeinen Knoten */
export function treeOf(f: { species: string; friendClass: string }) {
  return getLevelTree(f.species, f.friendClass);
}

/**
 * F8, F35: Was bei der nächsten offenen Wahl zur Auswahl steht.
 * Level 2: der Ast. Danach je Level zwei Verbesserungen im gewählten Ast; wo der Ast endet, allgemeine Knoten.
 */
export function levelChoices(f: RunFriend, rules: RunRules = runRules): (LevelNode | LevelBranch)[] {
  if (f.pendingLevels <= 0) return [];
  const tree = treeOf(f);
  const choiceLevel = f.level - f.pendingLevels + 1; // das Level, für das jetzt gewählt wird
  if (choiceLevel > rules.xp.choicesUntil) return [];
  if (!f.branch) return tree ? tree.branches : rules.genericUpgrades;
  const branch = tree?.branches.find((b) => b.id === f.branch);
  const idx = choiceLevel - 3;
  return branch?.upgrades[idx] ?? rules.genericUpgrades;
}

/** Wie viele Wahlen ein Level-Up bringt: nur bis Level 8 (F12) */
export function choicesBetween(fromLevel: number, toLevel: number, rules: RunRules = runRules): number {
  let n = 0;
  for (let l = fromLevel + 1; l <= toLevel; l++) if (l <= rules.xp.choicesUntil) n++;
  return n;
}

function nodeById(f: RunFriend, id: string): LevelNode | LevelBranch | undefined {
  const tree = treeOf(f);
  for (const b of tree?.branches ?? []) {
    if (b.id === id) return b;
    for (const level of b.upgrades) for (const n of level) if (n.id === id) return n;
  }
  return runRules.genericUpgrades.find((n) => n.id === id);
}

/** Alle Bausteine, die auf diesen Freund wirken: Ast, Knoten, Items, Artefakte */
export function effectsOf(f: RunFriend, run: Pick<RunState, 'artifacts'> | null): Effect[] {
  return sourcedEffectsOf(f, run).map((x) => x.e);
}

/** Wie effectsOf, aber jeder Baustein mit seiner Herkunft (Fabi, Phase Kern: Verlauf und Karte nennen den Grund) */
export function sourcedEffectsOf(f: RunFriend, run: Pick<RunState, 'artifacts'> | null): { e: Effect; via: string }[] {
  const list: { e: Effect; via: string }[] = [];
  const add = (effects: Effect[] | undefined, via: string) => {
    for (const e of effects ?? []) list.push({ e, via });
  };
  if (f.branch) add(nodeById(f, f.branch)?.effects, `branch:${f.branch}`);
  for (const id of f.nodes) add(nodeById(f, id)?.effects, `node:${id}`);
  // I24: Items mit ihrem gewürfelten Wert
  for (const id of f.items) add(itemEffects(id), `item:${baseItemId(id)}`);
  // I6: Verbrauchsitems für den nächsten Kampf
  for (const id of f.boosts ?? []) add(itemUse(id).next, `item:${baseItemId(id)}`);
  for (const id of run?.artifacts ?? []) add(getArtifact(id).effects, `artifact:${id}`);
  return list;
}

export function itemSlots(run: Pick<RunState, 'artifacts'> | null): number {
  let n = 3; // I1
  for (const id of run?.artifacts ?? []) for (const e of getArtifact(id).effects) if ('run' in e && e.run === 'itemSlots') n += e.add ?? 0;
  return n;
}

/** Werte, die der Freund im Kampf hat: Klasse × Level-Wachstum × Bausteine (F2, F7) */
export function combatProfile(f: RunFriend, run: Pick<RunState, 'artifacts'> | null, rules: RunRules = runRules): CombatProfile {
  const sp = getSpecies(f.species);
  const entry = sp.classes.find((c) => c.friendClass === f.friendClass)!;
  const base = friendStats(f.species, f.friendClass);
  const grow = (g: number) => 1 + g * (f.level - 1);
  // F37 (Fabi, Runde 7): Jeder Freund hat eigene Anlagen, in einem Rahmen um die Werte seiner Klasse gewürfelt
  const roll = (key: keyof RunRules['luck']['friend']) => (f.rolls?.[key] ?? 0) * rules.luck.friend[key];
  const stats: UnitStats = {
    ...base,
    hp: base.hp * grow(rules.levelGrowth.hp) * (1 + roll('hp')),
    damage: base.damage * grow(rules.levelGrowth.damage) * (1 + roll('damage')),
    attackInterval: base.attackInterval / (1 + roll('attackSpeed')),
    // K28 (Fabi, Runde 7): Tempo nach Klasse, Spezies und Anlage
    moveSpeed: base.moveSpeed * (sp.speed ?? 1) * (1 + roll('moveSpeed')),
    critChance: Math.max(0, base.critChance + roll('critChance')),
  };
  let attackHeal = (entry.attackHeal ?? 0) * grow(rules.levelGrowth.damage) * (1 + roll('damage'));
  let kniff: string | null = defaultKniff(entry);
  // R27: Zahlen und Sonderwirkungen, die nur in dieser Klasse gelten
  const params: Record<string, number> = { ...(entry.params ?? {}) };
  const perks: Record<string, number> = { ...(entry.perks ?? {}) };
  // Fabi (Phase Kern): woher jede Sonderwirkung kommt
  const perkFrom: Record<string, string[]> = Object.fromEntries(Object.keys(entry.perks ?? {}).map((k) => [k, [`class:${f.species}:${f.friendClass}`]]));
  let mutGain = 1;
  // Fabi (Runde 7): Start-Mut nicht immer gleich, sondern im Rahmen gewürfelt
  let startMut = Math.max(0, (entry.startMut ?? 0) + roll('startMut'));
  let mutNeed = entry.mutNeed ?? 1;
  const sourced = sourcedEffectsOf(f, run);
  const effects = sourced.map((x) => x.e);
  // Erst verwandelte Kniffe, dann Zahlen: Ein Knoten kann Werte des neuen Kniffs ändern
  for (const e of effects) if ('kniff' in e) kniff = e.kniff;
  for (const { e, via } of sourced) {
    if ('stat' in e) {
      const mul = e.mul ?? 1;
      const add = e.add ?? 0;
      switch (e.stat) {
        case 'attackSpeed':
          stats.attackInterval = stats.attackInterval / mul;
          break;
        case 'mutGain':
          mutGain *= mul;
          break;
        case 'startMut':
          startMut = startMut * mul + add;
          break;
        case 'mutNeed':
          mutNeed = mutNeed * mul + add;
          break;
        case 'attackHeal':
          attackHeal = attackHeal * mul + add;
          break;
        default:
          stats[e.stat] = stats[e.stat] * mul + add;
      }
    } else if ('param' in e) {
      const [ability, key] = e.param.split('.') as [string, string];
      const current = params[e.param] ?? getAbility(ability).params[key] ?? 0;
      params[e.param] = e.set !== undefined ? e.set : current * (e.mul ?? 1) + (e.add ?? 0);
    } else if ('perk' in e) {
      perks[e.perk] = (perks[e.perk] ?? 0) + e.value;
      const from = (perkFrom[e.perk] ??= []);
      if (!from.includes(via)) from.push(via);
    }
  }
  if (mutGain !== 1) perks.mutGain = mutGain;
  // K6 (Fabi): Start-Mut je nach Spezies und Klasse, Äste und Items können ihn erhöhen, höchstens ein Drittel
  if (startMut > 0) perks.startMut = Math.min(combatRules.mut.startMax, startMut);
  // K6 (Fabi): Wie viel Mut der Kniff braucht; nie weniger als ein Viertel [ZAHL?]
  if (Math.abs(mutNeed - 1) > 1e-9) perks.mutNeed = Math.max(0.25, mutNeed);
  // Q45 (Fabi, Spieltest Phase 4): Heilung und Schilde des Kniffs wachsen mit dem Level wie der Schaden,
  // damit sich ein Heiler, der schwach anfängt, zu leveln lohnt
  if (kniff) {
    const healGrow = grow(rules.levelGrowth.damage);
    for (const key of ['heal', 'allHeal', 'amount', 'shield', 'minAmount']) {
      const full = `${kniff}.${key}`;
      const base = params[full] ?? getAbility(kniff).params[key];
      if (base !== undefined) params[full] = base * healGrow;
    }
  }
  const profile: CombatProfile = { stats, attackHeal, kniff, params, perks, perkFrom, hp: f.hp };
  // F35 (Fabi, Spieltest Phase 4): Wenn ein Ast den Kniff verändert, stimmt auch seine Beschreibung
  const kniffText = f.branch ? treeOf(f)?.branches.find((b) => b.id === f.branch)?.kniffText : undefined;
  if (kniffText) profile.kniffText = kniffText;
  if (f.treeHp !== null) profile.treeHp = f.treeHp;
  return profile;
}

/** F35 (Spieltest Phase 4): So stünde der Freund mit dieser Wahl da; für Zahlen vorher und nachher beim Level-Up */
export function profileWithChoice(f: RunFriend, run: Pick<RunState, 'artifacts'> | null, choice: LevelNode | LevelBranch): CombatProfile {
  const g: RunFriend = 'upgrades' in choice ? { ...f, branch: choice.id } : { ...f, nodes: [...f.nodes, choice.id] };
  return combatProfile(g, run);
}

export function maxHp(f: RunFriend, run: Pick<RunState, 'artifacts'> | null): number {
  return combatProfile(f, run).stats.hp;
}

/** S5: Leben des Baums, wenn er ganz ist */
export function maxTreeHp(f: RunFriend, run: Pick<RunState, 'artifacts'> | null): number | null {
  if (getSpecies(f.species).passive !== 'baumhaus') return null;
  const p = combatProfile(f, run).params ?? {};
  return p['baumhaus.treeHp'] ?? getAbility('baumhaus').params.treeHp!;
}

/** F33: „Martin der Dachs“ */
export function displayName(f: { name: string; species: string }): string {
  const sp = getSpecies(f.species);
  return `${f.name} ${sp.article} ${sp.name}`;
}

/** Text eines Asts oder Knotens mit den Zahlen dieses Freundes: {name}, {p:faehigkeit.wert}, {pct:faehigkeit.wert} */
export function describe(text: string, f: RunFriend, run: Pick<RunState, 'artifacts'> | null, extra: Effect[] = []): string {
  const prof = combatProfile({ ...f, branch: f.branch, nodes: f.nodes }, run);
  // Für eine Vorschau: die Bausteine der Option schon mitrechnen
  const withExtra = extra.length ? previewParams(f, run, extra) : (prof.params ?? {});
  const after = extra.length ? previewProfile(f, run, extra) : prof;
  return fillNumbers(text.replaceAll('{name}', f.name), withExtra, after.stats.damage, extra.length ? { params: prof.params ?? {}, damage: prof.stats.damage } : undefined);
}

/**
 * F35 (Fabi, Abschlusstest Phase 4): Text eines Schritts im bisherigen Weg mit den Zahlen, die direkt nach diesem
 * Schritt galten, nicht mit den heutigen. step 0 ist der Ast (Level 2), step 1 die Wahl auf Level 3 und so weiter.
 * Es zählen der Weg bis einschließlich dieses Schritts und das Level dieses Schritts; Items, Stärkungen und Artefakte
 * gehören nicht zum Weg und bleiben draußen. Was der Schritt dazugebracht hat, steht wie beim Level-Up als „(+…)“ dabei.
 */
export function describeStep(text: string, f: RunFriend, step: number): string {
  const level = Math.max(1, Math.min(f.level, step + 2));
  // So stand der Freund nach den ersten n Schritten seines Wegs da (n = 0: noch keine Wahl)
  const upTo = (n: number): RunFriend => ({
    ...f,
    level,
    branch: n >= 1 ? f.branch : null,
    nodes: f.nodes.slice(0, Math.max(0, n - 1)),
    items: [],
    boosts: [],
    xpBoost: 0,
  });
  const before = combatProfile(upTo(step), null);
  const after = combatProfile(upTo(step + 1), null);
  return fillNumbers(text.replaceAll('{name}', f.name), after.params ?? {}, after.stats.damage, { params: before.params ?? {}, damage: before.stats.damage });
}

/** Wie der Freund mit zusätzlichen Bausteinen dastünde (nur Werte, für Texte) */
function previewProfile(f: RunFriend, run: Pick<RunState, 'artifacts'> | null, extra: Effect[]): CombatProfile {
  const base = combatProfile(f, run);
  const stats = { ...base.stats };
  for (const e of extra) if ('stat' in e && e.stat === 'damage') stats.damage = stats.damage * (e.mul ?? 1) + (e.add ?? 0);
  return { ...base, stats };
}

function previewParams(f: RunFriend, run: Pick<RunState, 'artifacts'> | null, extra: Effect[]): Record<string, number> {
  const params = { ...combatProfile(f, run).params };
  for (const e of extra) {
    if (!('param' in e)) continue;
    const [ability, key] = e.param.split('.') as [string, string];
    const current = params[e.param] ?? getAbility(ability).params[key] ?? 0;
    params[e.param] = e.set !== undefined ? e.set : current * (e.mul ?? 1) + (e.add ?? 0);
  }
  return params;
}

/** F37 (Fabi, Runde 7): Worte für Anlagen, bei denen ein Freund Glück hatte (Stern) */
export const STAR_WORDS = { hp: 'zäh', damage: 'kräftig', attackSpeed: 'schnell beim Zuschlagen', moveSpeed: 'flink auf den Pfoten', critChance: 'treffsicher', startMut: 'mutig' } as const;

/** F37: die Anlagen, bei denen ein Freund Glück hatte */
export function starTraits(f: Pick<RunFriend, 'rolls'>, rules: RunRules = runRules): string[] {
  return (Object.keys(STAR_WORDS) as (keyof typeof STAR_WORDS)[]).filter((k) => (f.rolls?.[k] ?? 0) >= rules.luck.starAt - 1e-9).map((k) => STAR_WORDS[k]);
}

/** „zäh, flink und mutig“ */
export const joinWords = (list: string[]): string => (list.length <= 1 ? (list[0] ?? '') : `${list.slice(0, -1).join(', ')} und ${list[list.length - 1]}`);
